/**
 * PlanStatusCard — dashboard widget showing plan, usage, and trial countdown.
 * TrialBanner    — top-of-page warning banner when trial is close to expiry.
 * UsageBar       — compact member/trainer usage bar for the sidebar.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Crown,
  Users,
  UserCog,
  Clock,
  ArrowRight,
  AlertTriangle,
  Zap,
  TrendingUp,
} from 'lucide-react';
import { usePlan, PLANS } from '../context/PlanContext';
import { UpgradeModal } from './UpgradeModal';

// ─── Usage Progress Bar ────────────────────────────────────────────────────
function UsageBar({ label, used, limit, color = '#3b82f6' }) {
  const pct = limit === Infinity ? 0 : Math.min(100, (used / limit) * 100);
  const near = pct >= 85;
  const barColor = pct >= 100 ? '#ef4444' : near ? '#f59e0b' : color;
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">{label}</span>
        <span className="text-xs font-bold" style={{ color: barColor }}>
          {used} / {limit === Infinity ? '∞' : limit}
        </span>
      </div>
      <div className="w-full h-1.5 bg-gray-100 dark:bg-white/8 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${limit === Infinity ? 0 : pct}%`, background: barColor }}
        />
      </div>
    </div>
  );
}

// ─── Plan Status Card (for Dashboard) ─────────────────────────────────────
export function PlanStatusCard() {
  const {
    plan,
    isTrial,
    trialDaysLeft,
    isExpired,
    memberLimit,
    trainerLimit,
    memberCount,
    trainerCount,
    nearMemberLimit,
    planId,
  } = usePlan();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const urgent = isTrial && trialDaysLeft <= 2;

  return (
    <>
      <div
        className={`premium-card ${urgent ? 'border-orange-500/40' : ''}`}
        style={{ padding: '20px 24px', borderColor: urgent ? 'rgba(249,115,22,0.3)' : undefined }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center ${planId === 'pro' ? 'bg-gradient-to-br from-yellow-400/20 to-orange-500/20' : 'bg-blue-500/10'}`}
            >
              {planId === 'pro' ? (
                <Crown size={16} className="text-yellow-500" />
              ) : (
                <Zap size={16} className="text-blue-500" />
              )}
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none mb-0.5">
                Current Plan
              </p>
              <p className="text-base font-black text-gray-900 dark:text-white leading-none">
                {plan.name}
              </p>
            </div>
          </div>
          {planId !== 'pro' && (
            <button
              onClick={() => setUpgradeOpen(true)}
              className="flex items-center gap-1.5 text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline"
            >
              Upgrade <ArrowRight size={12} />
            </button>
          )}
        </div>

        {/* Trial countdown */}
        {isTrial && (
          <div
            className={`flex items-center gap-2 rounded-xl px-3 py-2.5 mb-4 ${urgent ? 'bg-orange-500/10 border border-orange-500/20' : 'bg-blue-500/8 border border-blue-500/15'}`}
          >
            <Clock size={14} className={urgent ? 'text-orange-500' : 'text-blue-500'} />
            <span
              className={`text-xs font-bold ${urgent ? 'text-orange-500' : 'text-blue-600 dark:text-blue-400'}`}
            >
              {isExpired
                ? '⚠ Trial expired — upgrade to continue.'
                : `Trial ends in ${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''}`}
            </span>
          </div>
        )}

        {/* Usage bars */}
        <div className="flex flex-col gap-3">
          <UsageBar label="Members used" used={memberCount} limit={memberLimit} color="#3b82f6" />
          <UsageBar
            label="Trainers used"
            used={trainerCount}
            limit={trainerLimit}
            color="#a855f7"
          />
        </div>

        {/* Upgrade nudge */}
        {nearMemberLimit && planId !== 'pro' && (
          <div className="mt-4 flex items-start gap-2 text-[11px] text-amber-600 dark:text-amber-400 font-semibold">
            <TrendingUp size={13} className="mt-0.5 flex-shrink-0" />
            <span>
              {planId === 'trial'
                ? 'Your gym is growing. Upgrade to Starter to add more members.'
                : 'You are close to your member limit. Upgrade to Pro for unlimited members.'}
            </span>
          </div>
        )}

        {/* Upgrade button */}
        {planId !== 'pro' && (
          <button
            onClick={() => setUpgradeOpen(true)}
            className="mt-4 w-full py-2.5 rounded-xl text-xs font-bold bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-500 hover:to-blue-600 transition-all shadow-md shadow-blue-500/20 hover:-translate-y-0.5"
          >
            {planId === 'trial' ? 'Upgrade — from $25/mo' : 'Upgrade to Pro — $45/mo'}
          </button>
        )}
      </div>
      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        feature="the Pro plan"
      />
    </>
  );
}

// ─── Trial Banner (top of any page when trial is expiring) ─────────────────
export function TrialBanner() {
  const { isTrial, trialDaysLeft, isExpired } = usePlan();
  const [dismissed, setDismissed] = useState(false);
  if (!isTrial || trialDaysLeft > 3 || dismissed) {
    return null;
  }

  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-xl px-4 py-3 mb-6 text-sm font-semibold ${isExpired ? 'bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400' : 'bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-400'}`}
    >
      <div className="flex items-center gap-2">
        <AlertTriangle size={15} />
        <span>
          {isExpired
            ? 'Your free trial has expired. Upgrade to keep your gym data and continue.'
            : `Your free trial ends in ${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''}. Upgrade now to avoid interruption.`}
        </span>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        <Link
          to="/#pricing"
          className="px-3 py-1 bg-current/10 hover:bg-current/20 rounded-lg text-xs transition-colors"
        >
          Upgrade →
        </Link>
        <button
          onClick={() => setDismissed(true)}
          className="text-xs opacity-60 hover:opacity-100 transition-opacity"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

// ─── Sidebar plan badge (subtle, no white block) ──────────────────────────
export function SidebarPlanBadge() {
  const { isTrial, trialDaysLeft, planId, plan } = usePlan();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  if (planId === 'pro') {
    return null;
  } // pro users see nothing

  return (
    <>
      <div
        className="mx-3 mb-2 flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer group transition-colors hover:bg-orange-500/8 dark:hover:bg-orange-500/10"
        onClick={() => setUpgradeOpen(true)}
      >
        <div className="flex items-center gap-2">
          <Clock size={12} className="text-orange-500 flex-shrink-0" />
          <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            {isTrial ? `Trial · ${trialDaysLeft}d left` : plan.name}
          </span>
        </div>
        <span className="text-[10px] font-bold text-orange-500 group-hover:underline whitespace-nowrap">
          Upgrade
        </span>
      </div>
      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        feature="this feature"
      />
    </>
  );
}
